import { usePocketBase } from "@/context/pocketbase-context";
import { useQuery } from "@tanstack/react-query";

interface LearnLanguagesResponse {
  id: string;
  languageCode: string;
  proficiency: string;
  learningObjectives: string[];
}

export interface UserInformationResponse {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  gender: string;
  ageRange: string;
  country: string;
  nativeLanguage: string;
  learnLanguages: LearnLanguagesResponse[];
}

const coreUrl = import.meta.env.VITE_REACT_APP_POCKETBASE_URL;

const fetchUserInformation = async (
  token: string,
  userId: string,
): Promise<UserInformationResponse> => {
  const headers = new Headers();
  headers.append("Authorization", `Bearer ${token}`);

  if (!userId || userId.trim() === "") {
    return Promise.resolve({} as UserInformationResponse);
  }

  const response = await fetch(`${coreUrl}/api/user/${userId}/profile`, {
    method: "GET",
    headers: headers,
  });
  if (!response.ok) {
    throw new Error("Error when fetching user information");
  }

  return response.json() as Promise<UserInformationResponse>;
};

export const useUserInformation = () => {
  const { token, user } = usePocketBase();

  const userId = user?.id || "";

  return useQuery({
    queryKey: ["user-information", userId],
    queryFn: () => fetchUserInformation(token, userId),
    staleTime: 0,
    gcTime: 0,
  });
};
